import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Loader from '../Loader/Loader';
import getNewRoute from '../../containers/helpers';
import InSituGraph from './Graph/InSituGraph';

import Filters from '../FiltersSet/Filters';
import Card from '../Card/Card';
import ViewTitle from '../ViewTitle/ViewTitle';
import {
  useInSituGraphDataQuery,
  getInSituGraphData,
} from '../../services/api';

import './InSituView.scss';

const InSituView = ({ dateRange, colors }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const activeFilters = useMemo(
    () =>
      JSON.parse(searchParams?.get('activeFilters')) ?? [
        {
          stattype: undefined,
          system: undefined,
          leadtime: undefined,
          area: undefined,
          source: undefined,
          stat: undefined,
          dates: undefined,
        },
      ],
    [searchParams],
  );

  const logarithmicDisplay = useMemo(() => {
    const layoutDisplay = JSON.parse(searchParams?.get('layoutDisplay'));
    return layoutDisplay?.log !== 'on' ? 'off' : 'on';
  }, [searchParams]);

  const { data: graphData, isLoading: loadingGraphData1 } =
    useInSituGraphDataQuery(activeFilters);

  const { data: graphData2, isLoading: loadingGraphData2 } =
    useInSituGraphDataQuery(
      activeFilters.map(currentFilter => {
        if (currentFilter.stat === undefined) {
          return currentFilter;
        }
        return {
          ...currentFilter,
          stat: 1,
        };
      }),
    );

  const loadingGraphData = [loadingGraphData1, loadingGraphData2].some(
    item => item === true,
  );

  const addVisualization = useCallback(() => {
    const filters = [...activeFilters];
    filters.push(filters[filters.length - 1]);
    navigate(getNewRoute(location, { activeFilters: filters }));
  }, [activeFilters, location, navigate]);

  const removeVisualization = useCallback(
    index => {
      const filters = [...activeFilters];
      filters.splice(index, 1);
      navigate(getNewRoute(location, { activeFilters: filters }));
    },
    [activeFilters, location, navigate],
  );

  const updateLayout = useCallback(
    e => {
      let newLayoutDisplay = {};
      if (e.target.value === 'on') {
        newLayoutDisplay = {
          log: 'on',
        };
      }
      navigate(
        getNewRoute(location, {
          layoutDisplay: newLayoutDisplay,
        }),
      );
    },
    [location, navigate],
  );

  let FiltersView = <Loader />;
  let GraphView = <Loader />;
  let GraphDataView = <Loader />;
  let LogarithmicOption = null;

  let maxDepth = 0.0;
  if (graphData !== null) {
    graphData?.depth?.forEach(d => {
      const currentDepth = parseFloat(d);
      if (currentDepth > maxDepth) maxDepth = currentDepth;
    });
  }
  if (graphData2 !== null) {
    graphData2?.depth?.forEach(d => {
      const currentDepth = parseFloat(d);
      if (currentDepth > maxDepth) maxDepth = currentDepth;
    });
  }
  if (logarithmicDisplay === 'on') maxDepth = Math.log10(maxDepth);
  const customLayout = {
    yaxis: {
      title: 'depth',
      range: [maxDepth, 0],
      type: logarithmicDisplay === 'on' ? 'log' : '',
    },
  };

  GraphView = (
    <InSituGraph
      activeFilters={activeFilters}
      dateRange={dateRange}
      data={graphData}
      loading={loadingGraphData}
      color={colors}
      type="scatter"
      isExportable
      layout={customLayout}
    />
  );
  const customLayoutData = { ...customLayout, xaxis: { type: 'log' } };
  GraphDataView = (
    <InSituGraph
      activeFilters={activeFilters}
      dateRange={dateRange}
      data={graphData2}
      loading={loadingGraphData}
      color={colors}
      type="bar"
      isExportable
      layout={customLayoutData}
      handleCSVExport={() =>
        getInSituGraphData(activeFilters, {
          Accept: 'text/csv',
        })
      }
    />
  );
  LogarithmicOption = (
    <div className="LogarithmicOption">
      <h2
        style={{
          color: '#273b4b',
          fontWeight: 'bold',
          fontSize: 18,
          borderBottom: 'none',
          marginLeft: 20,
        }}
      >
        Logarithmic display
      </h2>
      <div
        style={{
          backgroundColor: '#273b4b',
          marginLeft: 20,
          width: '90%',
          height: 1,
          marginTop: 10,
          marginBottom: 20,
        }}
      />
      <span className="Button-radio">
        <input
          type="radio"
          name="logarithmicDisplay"
          id="logarithmicDisplay--on"
          value="on"
          onClick={updateLayout}
          defaultChecked={logarithmicDisplay === 'on'}
        />
        <label htmlFor="logarithmicDisplay--on">ON</label>
      </span>
      <span className="Button-radio">
        <input
          type="radio"
          name="logarithmicDisplay"
          id="logarithmicDisplay--off"
          value="off"
          onClick={updateLayout}
          defaultChecked={logarithmicDisplay === 'off'}
        />
        <label htmlFor="logarithmicDisplay--off">OFF</label>
      </span>
    </div>
  );

  if (!loadingGraphData) {
    FiltersView = (
      <Filters
        addVisualization={addVisualization}
        filters={activeFilters}
        removeVisualization={removeVisualization}
        view="vertical-profile"
      />
    );
  }

  if (location.pathname.startsWith('/bulletin')) {
    return (
      <div className="GraphView insitu">
        <Card className="graph no-border">
          {GraphView}
          {GraphDataView}
        </Card>
      </div>
    );
  }

  return (
    <>
      <ViewTitle pageTitle="Vertical Profile" />
      <div className="GraphView insitu">
        <Card className="graph">
          {GraphView}
          {GraphDataView}
        </Card>
        <Card className="filters">
          {FiltersView}
          {LogarithmicOption}
        </Card>
      </div>
    </>
  );
};
export default InSituView;
